import React, { useEffect, useRef } from 'react';
import { Dialog, useDialogState } from 'reakit';
import styled from 'styled-components';
import { Box } from '../Box';
import { Card } from '../Card';
import { Backdrop, CloseButton, usePreventBodyScroll } from '../Dialog';

const OverlayCard = styled(Card).attrs(() => ({ appearance: 'elevated' }))`
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    flex: 1;
    position: relative;

    &:focus {
        outline: none;
    }
`;

export interface OverlayProps {
    'aria-label': string;
    children: React.ReactNode;
    /**
     * An element ref to focus when the overlay is shown.
     */
    initialFocusRef?: React.RefObject<HTMLElement>;
    /**
     * Callback fired when the overlay is attempting to be closed.
     */
    onClose: () => void;
    /**
     * Dictates if the overlay is shown.
     */
    visible: boolean;
}

/**
 * An accessible modal-like component that follows the WAI-ARIA Dialog (Modal) pattern.
 *
 * Renders a near viewport sized container for housing content.
 */
export function Overlay({
    'aria-label': ariaLabel,
    initialFocusRef,
    children,
    onClose,
    visible,
}: OverlayProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const dialogProps = useDialogState();

    const controlledDialogProps = {
        ...dialogProps,
        hide: onClose,
        visible,
    };

    // We're using custom body scroll logic (along with `preventBodyScroll`) due to a bug in reakit
    // When moving to ariakit, this logic can be removed.
    usePreventBodyScroll(controlledDialogProps.visible);

    useEffect(() => {
        if (visible) {
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
            } else if (dialogRef?.current) {
                dialogRef.current.focus({
                    preventScroll: true,
                });
            }
        }
    }, [initialFocusRef, visible]);

    return (
        <Backdrop {...controlledDialogProps}>
            <Box display="flex" minHeight="100%" width="100%" paddingTop="3bu">
                <Dialog
                    ref={dialogRef}
                    as={OverlayCard}
                    aria-label={ariaLabel}
                    preventBodyScroll={false}
                    {...controlledDialogProps}
                >
                    {children}
                    <CloseButton onClose={onClose} />
                </Dialog>
            </Box>
        </Backdrop>
    );
}
